import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Link } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import MovieIcon from '@mui/icons-material/Movie';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import { withRouter } from 'react-router';

import { VpVideoCaptionPlayer } from '@vp-ui/components/VpVideoCaptionPlayer';
import { VpConditionalUpgradeWrapper } from '@vp-ui/components/VpConditionalUpgradeWrapper';
import { VpIcon } from '@vp-ui/components';
import config from '@src/config';
import {
  useCaptionPolling,
  CAPTION_POLLING_INTERVAL_MS,
  useGenerateCaption,
  useStartBurnIn,
  useFetchCaptionText,
  useUpdateCaptionText,
  useFetchCaptionUsage,
  useFetchMediaVersions,
  isVersionsDataProcessing
} from '@src/hooks/react-query/useCaptionsData';
import { useVpSnackbar } from '@vp-ui/hooks/useVpSnackbar';
import { useMediaTableContext } from '@src/hooks/contexts';
import { VpAddToLibraryButton, VpAddToPluginButton } from '@vp-ui/widgets/VpMediaTable/VpMediaSubTable';
import { useForceDownload } from '@src/hooks';
import {
  CAPTION_PRESETS,
  CAPTION_PRESET_KEYS,
  DEFAULT_PRESET,
  HIGHLIGHT_MODES,
  DEFAULT_HIGHLIGHT_MODE,
  WORDS_PER_DISPLAY,
  DEFAULT_WORDS_PER_DISPLAY,
  CROP_PRESETS,
  DEFAULT_CROP
} from '@src/constants/caption-presets';
import { CAPTION_LANGUAGES } from '@src/constants/caption-languages';
import { formatVersionLabel, getVersionLanguageLabel } from '@src/helpers/captionVersionHelpers';
import { selectedChipSx } from '@vp-ui/dialogs/VpMediaSubmissionDetailsDialog/captionStyles';

const VpMediaInfoCaptions = ({ media, history }) => {
  const [selectedPreset, setSelectedPreset] = useState(DEFAULT_PRESET);
  const [highlightMode, setHighlightMode] = useState(DEFAULT_HIGHLIGHT_MODE);
  const [wordsPerDisplay, setWordsPerDisplay] = useState(DEFAULT_WORDS_PER_DISPLAY);
  const [selectedCrop, setSelectedCrop] = useState(DEFAULT_CROP);
  const [captionText, setCaptionText] = useState('');
  const [isTextDirty, setIsTextDirty] = useState(false);
  const [selectedCaptionId, setSelectedCaptionId] = useState(null); // null = original

  const mediaId = media?.mediaId;
  const { setSelectedSubmissionId } = useMediaTableContext();
  const { data: versionsRaw } = useFetchMediaVersions(mediaId, {
    refetchInterval: (data) => (isVersionsDataProcessing(data) ? CAPTION_POLLING_INTERVAL_MS : false)
  });
  const versions = Array.isArray(versionsRaw) ? versionsRaw : [];
  const hasVersionProcessing = versions.some((v) => v.status === 'pending' || v.status === 'processing');

  const { data: captionsResponse, isLoading } = useCaptionPolling(mediaId);
  const { mutate: generateCaption, isLoading: isGenerating } = useGenerateCaption();
  const { mutate: startBurnInMutate, isLoading: isBurningIn } = useStartBurnIn(mediaId);
  const { mutate: updateCaptionTextMutate, isLoading: isSaving } = useUpdateCaptionText(mediaId);
  const { addSnackbarError, addSnackbarSuccess, addSnackbarInfo } = useVpSnackbar();
  const { data: usageData, isError: usageIsError, error: usageError } = useFetchCaptionUsage();
  const { forceDownload } = useForceDownload();

  // 403 means CAPTIONS_ON_DEMAND_ENABLED is disabled for this user's tier/org
  const isFeatureDisabled = usageIsError && usageError?.response?.status === 403;

  const captions = Array.isArray(captionsResponse) ? captionsResponse : [];
  const originalCaption = captions.find((c) => !c.isTranslation && c.status === 'completed');
  const translations = useMemo(
    () => captions.filter((c) => c.isTranslation && c.status === 'completed'),
    [captions]
  );
  const isProcessing = captions.some((c) => c.status === 'pending' || c.status === 'processing');
  const hasFailed = captions.some((c) => !c.isTranslation && c.status === 'failed');

  const activeCaption = selectedCaptionId
    ? translations.find((t) => t.id === selectedCaptionId) || originalCaption
    : originalCaption;

  const usedThisMonth = usageData?.used ?? 0;
  const monthlyLimit = usageData?.limit ?? -1;
  const isUnlimited = monthlyLimit === -1;
  const isAtLimit = !isUnlimited && usedThisMonth >= monthlyLimit;

  const { data: captionTextData } = useFetchCaptionText(activeCaption?.id);

  useEffect(() => {
    const text = captionTextData?.text || captionTextData || '';
    setCaptionText(text);
    setIsTextDirty(false);
  }, [captionTextData]);

  const handleGenerateCaption = () => {
    generateCaption(
      { mediaId },
      {
        onSuccess: () => addSnackbarInfo({ message: 'Generating captions. This usually takes 1–3 minutes.' }),
        onError: (error) => addSnackbarError({ error })
      }
    );
  };

  const handleSaveCaptionText = () => {
    const trimmedText = captionText.trim();
    if (!trimmedText) {
      addSnackbarInfo({
        message: 'Caption text cannot be empty',
        severity: 'error',
        title: 'Error'
      });
      return;
    }
    updateCaptionTextMutate(
      { captionId: activeCaption.id, text: trimmedText },
      {
        onSuccess: () => {
          addSnackbarSuccess({ message: 'Captions saved.' });
          setIsTextDirty(false);
        },
        onError: (error) => addSnackbarError({ error })
      }
    );
  };

  const handleBurnIn = () => {
    if (!activeCaption) return;
    startBurnInMutate(
      {
        captionId: activeCaption.id,
        preset: selectedPreset,
        highlightMode,
        wordsPerDisplay: highlightMode === HIGHLIGHT_MODES.WORD_BY_WORD ? wordsPerDisplay : undefined,
        crop: selectedCrop
      },
      {
        onSuccess: () => addSnackbarInfo({ message: 'Video processing. This may take a few minutes.' }),
        onError: (error) => addSnackbarError({ error })
      }
    );
  };

  if (isLoading) {
    return <CircularProgress size={24} />;
  }

  if (isFeatureDisabled) {
    return (
      <Stack gap={2} alignItems="flex-start">
        <Typography variant="body2" color="text.secondary">
          Captions are not available on your current plan.
        </Typography>
        <Button component={Link} to="/billing" variant="outlined" size="small">
          Upgrade Plan
        </Button>
      </Stack>
    );
  }

  return (
    <Stack gap={3}>
      {/* TODO: replace with captions-specific help center URL when available */}
      <Stack direction="row" justifyContent="flex-end">
        <Tooltip title="Learn more about captions">
          <IconButton
            component="a"
            href={config.helpCenterLink}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <VpIcon icon="HelpCircle" size="18px" />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* No captions state */}
      {!originalCaption && !isProcessing && !hasFailed && (
        <>
          <Typography variant="body2" color="text.secondary">
            No captions yet. Generate captions to enable subtitles and export options.
          </Typography>
          {usageData && (
            <Typography variant="caption" color={isAtLimit ? 'error' : 'text.secondary'}>
              {isUnlimited
                ? `${usedThisMonth} captions used this month (unlimited)`
                : `${usedThisMonth} / ${monthlyLimit} captions used this month`}
            </Typography>
          )}
          <VpConditionalUpgradeWrapper
            condition={isAtLimit}
            label="You've reached your monthly caption limit. Upgrade your plan to generate more captions."
          >
            <Button
              variant="contained"
              onClick={handleGenerateCaption}
              disabled={isGenerating || isAtLimit}
              startIcon={isGenerating ? <CircularProgress size={16} /> : null}
            >
              {isGenerating ? 'Starting...' : 'Generate Captions'}
            </Button>
          </VpConditionalUpgradeWrapper>
        </>
      )}

      {/* Processing state — caption not yet available */}
      {isProcessing && !originalCaption && (
        <Stack direction="row" gap={1} alignItems="center">
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Generating captions... This usually takes 1-3 minutes.
          </Typography>
        </Stack>
      )}

      {/* Failed state */}
      {hasFailed && !originalCaption && (
        <>
          <Typography variant="body2" color="error">Caption generation failed. Please try again.</Typography>
          <Button variant="outlined" size="small" onClick={handleGenerateCaption} disabled={isGenerating}>
            Retry
          </Button>
        </>
      )}

      {/* Main content — shown when caption exists */}
      {originalCaption && (
        <>
          {usageData && (
            <Typography variant="caption" color={isAtLimit ? 'error' : 'text.secondary'}>
              {isUnlimited
                ? `${usedThisMonth} captions used this month (unlimited)`
                : `${usedThisMonth} / ${monthlyLimit} captions used this month`}
            </Typography>
          )}

          {/* Inline video player with captions */}
          <VpVideoCaptionPlayer
            mediaUrl={media?.mediaUrl}
            captions={activeCaption ? [activeCaption] : []}
            thumbnailUrl={media?.thumbnailUrl}
            preset={selectedPreset}
            highlightMode={highlightMode}
            wordsPerDisplay={wordsPerDisplay}
          />

          {/* Language selector — shown when translations are available */}
          {translations.length > 0 && (
            <Stack gap={1}>
              <Typography variant="body2" color="text.secondary">Caption Language</Typography>
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Chip
                  label="Original"
                  variant="outlined"
                  size="small"
                  onClick={() => setSelectedCaptionId(null)}
                  sx={selectedCaptionId === null ? selectedChipSx : undefined}
                />
                {translations.map((t) => {
                  const lang = CAPTION_LANGUAGES.find((l) => l.code === t.targetLanguage);
                  const isSelected = selectedCaptionId === t.id;
                  return (
                    <Chip
                      key={t.id}
                      label={lang?.label || t.targetLanguage}
                      variant="outlined"
                      size="small"
                      onClick={() => setSelectedCaptionId(t.id)}
                      sx={isSelected ? selectedChipSx : undefined}
                    />
                  );
                })}
              </Stack>
            </Stack>
          )}

          {/* Caption text editor */}
          <Stack gap={1}>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <EditIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>Caption Text</Typography>
              <Typography variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                — edit to correct transcription errors
              </Typography>
            </Stack>
            <TextField
              multiline
              minRows={5}
              maxRows={10}
              value={captionText}
              onChange={(e) => {
                setCaptionText(e.target.value);
                setIsTextDirty(true);
              }}
              placeholder="Caption text will appear here..."
              size="small"
              fullWidth
            />
            {isTextDirty && (
              <Button
                variant="contained"
                size="small"
                onClick={handleSaveCaptionText}
                disabled={isSaving}
                sx={{ alignSelf: 'flex-end' }}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            )}
          </Stack>

          <Stack gap={1}>
            <Typography variant="body2" color="text.secondary">Highlight Mode</Typography>
            <ToggleButtonGroup
              value={highlightMode}
              exclusive
              onChange={(_, value) => value && setHighlightMode(value)}
              size="small"
              aria-label="Highlight mode"
            >
              <ToggleButton value={HIGHLIGHT_MODES.KARAOKE}>Karaoke</ToggleButton>
              <ToggleButton value={HIGHLIGHT_MODES.WORD_BY_WORD}>Word by Word</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {highlightMode === HIGHLIGHT_MODES.WORD_BY_WORD && (
            <Stack gap={1}>
              <Typography variant="body2" color="text.secondary">Words per Display</Typography>
              <ToggleButtonGroup
                value={wordsPerDisplay}
                exclusive
                onChange={(_, value) => value && setWordsPerDisplay(value)}
                size="small"
                aria-label="Words per display"
              >
                <ToggleButton value={WORDS_PER_DISPLAY.ONE}>1 Word</ToggleButton>
                <ToggleButton value={WORDS_PER_DISPLAY.THREE}>3 Words</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          )}

          <Stack gap={1}>
            <Typography variant="body2" color="text.secondary">Caption Style</Typography>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {CAPTION_PRESET_KEYS.map((key) => (
                <Chip
                  key={key}
                  label={CAPTION_PRESETS[key].label}
                  variant="outlined"
                  onClick={() => setSelectedPreset(key)}
                  size="small"
                  sx={selectedPreset === key ? selectedChipSx : undefined}
                />
              ))}
            </Stack>
          </Stack>

          <Stack gap={1}>
            <Typography variant="body2" color="text.secondary">Aspect Ratio</Typography>
            <ToggleButtonGroup
              value={selectedCrop}
              exclusive
              onChange={(_, value) => value && setSelectedCrop(value)}
              size="small"
              aria-label="Aspect ratio"
            >
              {CROP_PRESETS.map((crop) => (
                <ToggleButton key={crop.value} value={crop.value}>{crop.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>

          <Stack direction="row" gap={1} alignItems="center">
            <Button
              variant="contained"
              size="small"
              onClick={handleBurnIn}
              disabled={isBurningIn || hasVersionProcessing}
              startIcon={
                isBurningIn || hasVersionProcessing
                  ? <CircularProgress size={16} />
                  : <MovieIcon />
              }
            >
              {isBurningIn || hasVersionProcessing ? 'Processing...' : 'Process Video'}
            </Button>
          </Stack>

          {versions.length > 0 && (
            <Stack gap={1}>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>Processed Videos</Typography>
              {versions.map((version) => {
                const fileName = version.burnInUrl
                  ? version.burnInUrl.split('/').pop().split('?')[0]
                  : `${formatVersionLabel(version)}.mp4`;
                return (
                  <Stack key={version.burnInId || version.captionId} direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                      <Chip
                        label={formatVersionLabel(version)}
                        size="small"
                        variant="outlined"
                        color={version.status === 'failed' ? 'error' : 'default'}
                      />
                      {getVersionLanguageLabel(version) && (
                        <Chip
                          label={getVersionLanguageLabel(version)}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {version.preset && (
                        <Typography variant="caption" color="text.secondary">
                          {version.preset} · {version.crop}
                          {version.highlightMode ? ` · ${version.highlightMode}` : ''}
                        </Typography>
                      )}
                      {(version.status === 'pending' || version.status === 'processing') && <CircularProgress size={14} />}
                    </Stack>
                    <Stack direction="row" alignItems="center" gap={0.5} flexShrink={0}>
                      {version.status === 'completed' && version.burnInUrl && (
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            onClick={() => forceDownload(version.burnInUrl, fileName)}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {version.status === 'completed' && version.burnInUrl && (
                        <VpAddToLibraryButton
                          mediaId={mediaId}
                          burnInId={version.burnInId}
                        />
                      )}
                      {version.status === 'completed' && version.burnInUrl && (
                        <VpAddToPluginButton
                          mediaId={mediaId}
                          burnInId={version.burnInId}
                        />
                      )}
                      {version.status === 'completed' && mediaId && (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => {
                            setSelectedSubmissionId(null);
                            history.push('/media', { pendingExpandVersions: mediaId });
                          }}
                        >
                          View in Media Tab
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </>
      )}

    </Stack>
  );
};

VpMediaInfoCaptions.propTypes = {
  media: PropTypes.shape({
    mediaId: PropTypes.string,
    mediaUrl: PropTypes.string,
    thumbnailUrl: PropTypes.string
  }),
  history: PropTypes.object
};

export default withRouter(VpMediaInfoCaptions);



// WEBPACK FOOTER //
// ./src/vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpMediaInfoCaptions.jsx